<script lang="ts" setup>
import { computed, ref } from 'vue'
import { tryFetchGet } from '@/Support/Routing/Functions/tryFetch'
import { routing } from '@/Support/Routing/Functions/routing'
import LoadingBox from '@/Components/App/Global/LoadingBox.vue'
import { t } from '@/Support/lang'
import ChatSmIcon from '@/Components/App/Global/Icons/ChatSmIcon.vue'
import EmptyBox from '@/Components/App/Global/EmptyBox.vue'
import { AiConversationsData } from '@/Support/AI/Interfaces/AiConversationData'
import AiConversationMenu from '@/Components/App/AI/AiConversationMenu.vue'

defineProps<{
  removalLoading?: boolean
}>()

const conversationsData = ref<AiConversationsData | null>(null)
const conversations = computed(() => conversationsData.value?.data || [])
const loading = ref(true)
const load = async () => {
  loading.value = true
  const response = await tryFetchGet(routing('ai.conversations.index'))

  if (response) {
    conversationsData.value = response.data as AiConversationsData
    conversations.value.forEach(
      conversation => (confirmRemovals.value[conversation.id] = false),
    )
  }
  loading.value = false
}
defineExpose({
  load,
})

const emit = defineEmits<{
  (e: 'load', value: string): void
  (e: 'remove', value: string): void
}>()

// Removal
const confirmRemovals = ref<{ [index: string]: boolean }>({})
</script>
<template>
  <LoadingBox
    class="mt-4 mx-4"
    v-if="loading || removalLoading"
    :loading="loading || removalLoading"
    :title="
      loading
        ? t('ai.query.loading-box.threads')
        : t('ai.query.loading-box.delete')
    "
  />
  <div v-else-if="conversations.length === 0" class="p-4">
    <EmptyBox
      class="h-full"
      title="ai.query.empty-box.threads"
      :icon="ChatSmIcon"
      icon-class="opacity-30"
    />
  </div>
  <ul
    v-else
    role="list"
    class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 h-96 overflow-auto bg-white dark:bg-neutral-800 rounded-t-lg divide-y divide-neutral-100 dark:divide-neutral-700"
  >
    <li
      v-for="conversation in conversations"
      :key="conversation.id"
      @dblclick="emit('load', conversation.id)"
    >
      <div
        class="bg-danger-50/50 dark:bg-danger-900/25 flex flex-col px-4 py-2 rounded-lg"
        v-if="confirmRemovals[conversation.id]"
      >
        <p class="text-neutral-900 dark:text-neutral-100 text-sm mb-4">
          {{ t('ai.query.menu.confirm-remove-description') }}
        </p>
        <div class="flex space-x-2 justify-end">
          <button
            class="cursor-pointer bg-transparent px-1.5 py-1 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-300 dark:text-neutral-300 dark:bg-neutral-900 text-xs rounded-sm"
            @click="confirmRemovals[conversation.id] = false"
          >
            {{ t('Cancel') }}
          </button>
          <button
            class="cursor-pointer bg-transparent px-1.5 py-1 text-danger-700 hover:text-danger-900 hover:bg-danger-300 dark:text-danger-300 dark:bg-danger-900 text-xs rounded-sm"
            @click="emit('remove', conversation.id)"
          >
            {{ t('ai.query.menu.confirm-remove-button') }}
          </button>
        </div>
      </div>
      <div
        v-else
        class="flex gap-x-4 py-5 hover:bg-neutral-50 dark:hover:bg-neutral-900 relative"
      >
        <img
          class="size-12 flex-none rounded-full bg-neutral-50"
          :src="conversation.user.profilePhotoUrl"
          :alt="conversation.user.name"
        />
        <div class="flex-auto">
          <div class="flex items-baseline justify-between gap-x-4">
            <p
              class="text-sm/6 font-semibold text-neutral-900 dark:text-neutral-100"
            >
              {{ conversation.user.name }}
            </p>
            <p class="flex-none text-xs text-neutral-600 dark:text-neutral-400">
              <time :datetime="conversation.date"
                >{{ conversation.humanDate }}
              </time>
            </p>
          </div>
          <p
            v-if="conversation.name.length"
            class="mt-1 line-clamp-2 text-sm/6 text-neutral-600 dark:text-neutral-300"
          >
            {{ conversation.name }}
          </p>
          <p v-else class="mt-1 text-sm/6 text-danger-600 dark:text-danger-300">
            {{ t('ai.query.thread-empty-name') }}
          </p>
        </div>
        <AiConversationMenu
          @load="emit('load', conversation.id)"
          @remove="confirmRemovals[conversation.id] = true"
        />
      </div>
    </li>
  </ul>
</template>
